import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { format } from "date-fns";
import { Activity, AlertCircle } from "lucide-react";

interface CustomerActivityTabProps {
  tenantId: string;
}

interface AuditLogEntry {
  id: string;
  action: string;
  entity_type: string;
  actor_email: string | null;
  created_at: string;
  old_values: Record<string, unknown> | null;
  new_values: Record<string, unknown> | null;
  metadata: Record<string, unknown> | null;
}

const actionColors: Record<string, string> = {
  tenant_created: "bg-green-500/10 text-green-500 border-green-500/20",
  tenant_updated: "bg-blue-500/10 text-blue-500 border-blue-500/20",
  tenant_suspended: "bg-red-500/10 text-red-500 border-red-500/20",
  tenant_reactivated: "bg-green-500/10 text-green-500 border-green-500/20",
  tenant_deleted: "bg-red-500/10 text-red-500 border-red-500/20",
  impersonation_started: "bg-yellow-500/10 text-yellow-500 border-yellow-500/20",
};

const actionLabels: Record<string, string> = {
  tenant_created: "Created",
  tenant_updated: "Updated",
  tenant_suspended: "Suspended",
  tenant_reactivated: "Reactivated",
  tenant_deleted: "Deleted",
  impersonation_started: "Impersonation Started",
};

export function CustomerActivityTab({ tenantId }: CustomerActivityTabProps) {
  const { data: auditLogs, isLoading, error } = useQuery({
    queryKey: ["tenant-activity", tenantId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("calie_billing_audit_log")
        .select("*")
        .eq("tenant_id", tenantId)
        .order("created_at", { ascending: false })
        .limit(50);

      if (error) throw error;
      return data as AuditLogEntry[];
    },
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Activity Log</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-start gap-4">
              <Skeleton className="h-10 w-10 rounded-full" />
              <div className="space-y-2 flex-1">
                <Skeleton className="h-4 w-1/3" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="flex flex-col items-center text-center">
            <AlertCircle className="h-12 w-12 text-destructive mb-4" />
            <p className="text-muted-foreground">Failed to load activity log</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!auditLogs || auditLogs.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Activity Log</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Activity className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold">No Activity Yet</h3>
            <p className="text-muted-foreground mt-2">
              Activity will appear here as actions are performed on this customer account.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Activity Log</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {auditLogs.map((log) => (
            <div
              key={log.id}
              className="flex items-start gap-4 p-4 rounded-lg border bg-card"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge
                    variant="outline"
                    className={actionColors[log.action] || ""}
                  >
                    {actionLabels[log.action] || log.action}
                  </Badge>
                  <span className="text-sm text-muted-foreground">
                    by {log.actor_email || "System"}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  {format(new Date(log.created_at), "MMM d, yyyy 'at' h:mm a")}
                </p>
                {log.old_values && log.new_values && (
                  <div className="mt-2 text-sm">
                    <span className="text-muted-foreground">Changed: </span>
                    {Object.keys(log.new_values).map((key) => (
                      <span key={key} className="mr-2">
                        <span className="font-medium">{key}</span>:{" "}
                        <span className="line-through text-muted-foreground">
                          {String((log.old_values as Record<string, unknown>)[key])}
                        </span>{" "}
                        →{" "}
                        <span>
                          {String((log.new_values as Record<string, unknown>)[key])}
                        </span>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
